// AdminLoginPage.js - Login page for agency and super admins
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthShell, { AuthField, AuthErrorBanner, AuthPrimaryButton, AuthPasswordInput, AUTH_INPUT } from './AuthShell';

const AdminLoginPage = ({ title = "Admin Portal", type = "agency", agencyCode }) => {
  const navigate = useNavigate();
  const { login: authLogin, checkAuth, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authLogin({
        email: formData.email,
        password: formData.password,
        adminType: type,
        agencyCode: agencyCode
      });
      
      // Get user data from response - handle the nested response structure
      // Backend returns: { success, data: { token, user }, message, timestamp }
      const loginUser = response.data?.data?.user || response.data?.user || response.user || response.data;
      console.log('Login response:', { response, user: loginUser, fullResponseData: response.data });
      
      // Fetch full profile to get status field
      await checkAuth();
      
      // Wait for state update, then use user from context
      setTimeout(() => {
        const currentUser = user || loginUser;
        console.log('Navigating based on user role:', currentUser.role, 'status:', currentUser.status);
        
        // Check for commission-only agents first
        if (currentUser.role === 'agent' && currentUser.status === 'commission_only') {
          console.log('Commission-only agent detected, redirecting to commissions-only page');
          navigate('/agent/commissions-only');
          return;
        }
        
        // Navigate based on user role
        if (currentUser.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'agency_admin') {
          // Redirect agency admins directly to /admin/dashboard (not legacy /agency/dashboard)
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'agents_manager') {
          // Sub-admin role for agent management
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'commissions_manager') {
          // Sub-admin role for commission management
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'content_manager') {
          navigate('/admin/resources');
        } else if (currentUser.role === 'agent') {
          navigate('/agent/dashboard');
        } else {
          // If somehow a non-admin logs in here, send them to agent dashboard
          navigate('/agent/dashboard');
        }
      }, 200);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const isSuper = type === 'super';

  return (
    <AuthShell>
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
          style={{ backgroundColor: isSuper ? '#7F1D1D' : 'var(--primary)' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
          {title}
        </h2>
        <p className="text-sm text-[var(--muted-foreground)] mt-1">Secure administrator access only</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthField label="Admin Email" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@agency.com"
            className={AUTH_INPUT}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </AuthField>

        <AuthField label="Password" htmlFor="password">
          <AuthPasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
        </AuthField>

        <AuthErrorBanner>{error}</AuthErrorBanner>

        <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Authenticating…">
          Sign In
        </AuthPrimaryButton>

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/admin/forgot-password')}
            className="text-xs font-semibold hover:underline transition-colors"
            style={{ color: 'var(--accent)' }}
          >
            Forgot your password?
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-xs text-[var(--muted-foreground)]">
        <p className="mb-1">⚠️ Authorized personnel only</p>
        {isSuper && (
          <p className="text-red-600">
            This is a super administrator portal with full system access.
            All actions are logged and monitored.
          </p>
        )}
      </div>
    </AuthShell>
  );
};

export default AdminLoginPage;
